import { useEffect, useState } from 'react';
import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import OrgsService from '../../../services/orgs';
import { getKycIsInherited, getPrimaryKycData } from '../services/kyc';
import { extractErrorMessage } from '../../../util/errorMessage';
import crawlAncestorHierarchy from '../services/crawlAncestorHierarchy';
import { StripeEnv } from '../../../util/enums';
import { getKycById } from '../../../services/vault/kyc';
import usePropTimer from '../../../hooks/usePropTimer';
import { AWAIT_PROP_TIMEOUT_SECONDS } from '../../../const';

export default function usePrimaryKyc({
  org,
  orgDataIsFinished,
  financialAccountNode,
  financialAccountIsFinished,
  provider,
  stripeEnv,
  currentUserId,
  awaitCurrentUserId,
  financialAccountRoots,
  financialAccountRootsAreFinished,
  payableOrgId,
}: {
  org?: OrgNode;
  orgDataIsFinished: boolean;
  financialAccountNode?: FinancialAccountNode;
  financialAccountIsFinished: boolean;
  provider: VaultProvider;
  stripeEnv: StripeEnv;
  currentUserId?: string;
  awaitCurrentUserId: boolean;
  financialAccountRoots?: FinancialAccountNode[];
  financialAccountRootsAreFinished: boolean;
  payableOrgId?: string;
}): {
  primaryKyc?: Kyc;
  errorMessage?: string;
  isFinished: boolean;
  representativeUserId?: string;
  representativeStripePersonId?: string;
  replaceRepresentative: boolean;
} {
  const [primaryKyc, setPrimaryKyc] = useState<Kyc>();
  const [representativeUserId, setRepresentativeUserId] = useState<string>();
  const [representativeStripePersonId, setRepresentativeStripePersonId] =
    useState<string>();
  const [replaceRepresentative, setReplaceRepresentative] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFinished, setIsFinished] = useState(false);
  usePropTimer({
    onTimeout: () => {
      setErrorMessage(
        `Have not received currentUserId after ${AWAIT_PROP_TIMEOUT_SECONDS}s`,
      );
      setIsFinished(true);
    },
    awaitProp: awaitCurrentUserId,
    propToAwait: currentUserId,
  });

  useEffect(() => {
    const gatherData = async (): Promise<void> => {
      if (!orgDataIsFinished) {
        return;
      }

      if (financialAccountIsFinished && financialAccountNode === undefined) {
        setIsFinished(true);
      } else if (
        primaryKyc === undefined &&
        !!financialAccountNode?.finAcctId &&
        financialAccountRootsAreFinished
      ) {
        try {
          const retrievedKycData = await getPrimaryKycData(
            financialAccountNode.finAcctId,
            provider,
          );
          const createdByCurrentUser =
            !!retrievedKycData.userId &&
            !!currentUserId &&
            retrievedKycData.userId === currentUserId;
          const isInherited =
            retrievedKycData?.kyc?.id !== undefined &&
            financialAccountRoots !== undefined
              ? await getKycIsInherited(
                  retrievedKycData.kyc.id,
                  financialAccountRoots,
                )
              : false;

          setRepresentativeUserId(retrievedKycData.userId);
          setRepresentativeStripePersonId(retrievedKycData.stripePersonId);
          if (retrievedKycData.kyc !== undefined) {
            setPrimaryKyc(retrievedKycData.kyc);
          }

          if (retrievedKycData.kyc === undefined) {
            setReplaceRepresentative(true);
          }

          if (
            retrievedKycData.kyc?.id &&
            financialAccountRoots &&
            financialAccountRoots.length > 0 &&
            !createdByCurrentUser &&
            !isInherited &&
            org
          ) {
            setReplaceRepresentative(true);
            const ancestorFinancialAccount = await crawlAncestorHierarchy(
              org,
              financialAccountRoots,
              provider,
              stripeEnv,
              payableOrgId,
            );
            if (ancestorFinancialAccount?.finAcctId) {
              const ancestorFinancialAccountUser =
                await OrgsService.getAccountRepresentative(
                  ancestorFinancialAccount.finAcctId,
                );
              const ancestorKyc = await getKycById(
                ancestorFinancialAccountUser.kycId,
              );
              setPrimaryKyc(ancestorKyc);
            }
          }
        } catch (e) {
          setErrorMessage(extractErrorMessage(e));
        } finally {
          setIsFinished(true);
        }
      }
    };
    gatherData();
  }, [
    currentUserId,
    financialAccountIsFinished,
    financialAccountNode,
    financialAccountNode?.finAcctId,
    financialAccountRoots,
    financialAccountRootsAreFinished,
    primaryKyc,
    provider,
    org,
    stripeEnv,
    orgDataIsFinished,
    payableOrgId,
  ]);

  return {
    primaryKyc,
    errorMessage,
    isFinished,
    representativeUserId,
    representativeStripePersonId,
    replaceRepresentative,
  };
}
